import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiHash, FiSearch, FiDownload, FiPrinter, FiPackage, FiBox, FiCheckCircle, FiXCircle, FiRotateCcw } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import ItemSelect from '../../components/ItemSelect';
import { getStockFlowByCriteria } from '../../utils/stockFlowHelper';
import { TRAN_TYPES } from '../../config/tranTypes';
import { TRAN_TYPE_ROUTES } from '../../utils/navigationHelper';
import { formatDate } from '../../utils/formatters';
import useReportMemory from '../../hooks/useReportMemory';

// Styled Components
const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 160px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const DateInput = styled.input`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  background: white;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: ${colors.text.secondary};
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px 10px 40px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;

const Button = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  ${props => props.primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover {
      background: ${colors.primary.dark};
    }
  ` : `
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
    &:hover {
      background: ${colors.neutral[200]};
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const SummaryInfo = styled.div``;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0;
  margin-bottom: 20px;
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const Tab = styled.button`
  flex: 1;
  padding: 14px 24px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
  background: ${props => props.active ? colors.primary.main : 'white'};
  color: ${props => props.active ? 'white' : colors.text.secondary};
  border-bottom: 3px solid ${props => props.active ? colors.primary.dark : 'transparent'};

  &:hover {
    background: ${props => props.active ? colors.primary.main : colors.neutral[50]};
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: ${props => props.align || 'left'};
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
  white-space: nowrap;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.2s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
  white-space: nowrap;
`;

const ItemCode = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
  padding: 4px 8px;
  background: ${colors.neutral[100]};
  border-radius: ${colors.borderRadius.sm};
  color: ${colors.text.secondary};
`;

const SerialBadge = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
  padding: 4px 8px;
  background: ${colors.primary.main}10;
  border-radius: ${colors.borderRadius.sm};
  color: ${colors.primary.main};
  font-weight: 500;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    switch (props.status) {
      case 'In Stock': return colors.success.main;
      case 'Issued': return colors.error.main;
      case 'Returned': return colors.warning.main;
      default: return colors.neutral[400];
    }
  }}15;
  color: ${props => {
    switch (props.status) {
      case 'In Stock': return colors.success.main;
      case 'Issued': return colors.error.main;
      case 'Returned': return colors.warning.main;
      default: return colors.neutral[600];
    }
  }};
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 11px;
  font-weight: 500;
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 13px;
`;

const getTranTypeName = (code) => {
  const tranType = TRAN_TYPES[code];
  return tranType ? tranType.name : code || '-';
};

const SerialTracking = () => {
  const { activeCompany, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('serial-tracking');
  const navigate = useNavigate();

  // Clickable doc-number cell for a serial/batch received/issued document.
  const docCell = (docNumber, tranTypeCode, txId) => {
    if (docNumber && TRAN_TYPE_ROUTES[tranTypeCode] && txId) {
      return <span onClick={() => navigate(TRAN_TYPE_ROUTES[tranTypeCode], { state: { viewTransactionId: txId } })}
        title="Open document" style={{ color: colors.primary.main, cursor: 'pointer', fontWeight: 500 }}>{docNumber}</span>;
    }
    return docNumber || '-';
  };

  const [activeTab, setActiveTab] = useState(() => savedMem?.activeTab ?? 'serial'); // 'serial' or 'batch'
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(() => savedMem?.hasGenerated ?? false);

  // Serial tracking data
  const [serialData, setSerialData] = useState(() => savedMem?.serialData ?? []);
  // Batch tracking data
  const [batchData, setBatchData] = useState(() => savedMem?.batchData ?? []);

  // Filters
  const [selectedItem, setSelectedItem] = useState(() => savedMem?.selectedItem ?? 'all');
  const [selectedLocation, setSelectedLocation] = useState(() => savedMem?.selectedLocation ?? 'all');
  const [selectedStatus, setSelectedStatus] = useState(() => savedMem?.selectedStatus ?? 'all');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [searchQuery, setSearchQuery] = useState(() => savedMem?.searchQuery ?? '');

  // Summary
  const [summary, setSummary] = useState(() => savedMem?.summary ?? {
    totalTracked: 0,
    inStock: 0,
    issued: 0,
    returned: 0
  });

  const loadMasterData = useCallback(async () => {
    if (!activeCompany?.id) return;

    const itemsRef = collection(db, `companies/${activeCompany.id}/inventoryItems`);
    const itemsSnapshot = await getDocs(query(itemsRef, orderBy('itemCode')));
    const itemsList = itemsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })).filter(i => i.isDeleted !== true);
    setItems(itemsList);

    const locRef = collection(db, `companies/${activeCompany.id}/inventoryLocations`);
    const locSnapshot = await getDocs(query(locRef, orderBy('locationCode')));
    const locList = locSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setLocations(locList);
  }, [activeCompany]);

  const loadTrackingData = useCallback(async () => {
    if (!activeCompany?.id) return;

    try {
      setLoading(true);

      const criteria = { status: 'Posted' };
      if (selectedItem !== 'all') criteria.itemId = selectedItem;
      if (selectedLocation !== 'all') criteria.locationId = selectedLocation;

      let rawEntries = await getStockFlowByCriteria(activeCompany.id, criteria);

      // Branch filter
      rawEntries = rawEntries.filter(t => {
        if (!activeBranch || activeBranch.id === 'all') return true;
        return t.branchId === activeBranch.id;
      });

      // Build item master lookup
      const itemMasterMap = {};
      items.forEach(item => {
        itemMasterMap[item.id] = item;
      });

      // Date filter
      let filtered = rawEntries;
      if (dateFrom) {
        filtered = filtered.filter(e => (e.docDate || '') >= dateFrom);
      }
      if (dateTo) {
        filtered = filtered.filter(e => (e.docDate || '') <= dateTo);
      }

      // Fill missing item data from master
      filtered = filtered.map(entry => {
        const master = itemMasterMap[entry.itemId];
        if (!master) return entry;
        const patched = { ...entry };
        if (!patched.itemCode) patched.itemCode = master.itemCode || '';
        if (!patched.itemName) patched.itemName = master.itemName || '';
        return patched;
      });

      // Process serial numbers
      processSerialData(filtered, itemMasterMap);

      // Process batch data
      processBatchData(filtered, itemMasterMap);

    } catch (error) {
      console.error('Error loading tracking data:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, selectedItem, selectedLocation, dateFrom, dateTo, items, activeBranch]);

  const processSerialData = (entries, itemMasterMap) => {
    // Build a map of serial number -> lifecycle
    const serialMap = {};

    // Sort entries by date for correct lifecycle ordering
    const sorted = [...entries].sort((a, b) => (a.docDate || '').localeCompare(b.docDate || ''));

    sorted.forEach(entry => {
      const serials = entry.serialNumbers || [];
      if (serials.length === 0) return;

      serials.forEach(serialNumber => {
        const key = `${entry.itemId}__${serialNumber}`;

        if (!serialMap[key]) {
          serialMap[key] = {
            serialNumber,
            itemId: entry.itemId,
            itemCode: entry.itemCode || '',
            itemName: entry.itemName || '',
            status: 'In Stock',
            receivedVia: '',
            receivedDate: '',
            receivedDocNumber: '',
            issuedVia: '',
            issuedDate: '',
            issuedDocNumber: '',
            locationName: entry.locationName || '',
            partyName: '',
            movements: []
          };
        }

        const record = serialMap[key];

        record.movements.push({
          direction: entry.direction,
          tranTypeCode: entry.tranTypeCode,
          docNumber: entry.docNumber,
          docDate: entry.docDate,
          locationName: entry.locationName
        });

        if (entry.direction === 'in') {
          // Check if this is a return (SR = Sale Return)
          if (record.issuedDate && entry.tranTypeCode === 'SR') {
            record.status = 'Returned';
            record.receivedVia = getTranTypeName(entry.tranTypeCode);
            record.receivedDate = entry.docDate || '';
            record.receivedDocNumber = entry.docNumber || '';
            record.receivedTxId = entry.transactionId;
            record.receivedTranType = entry.tranTypeCode;
          } else if (!record.receivedDate) {
            // First receipt
            record.receivedVia = getTranTypeName(entry.tranTypeCode);
            record.receivedDate = entry.docDate || '';
            record.receivedDocNumber = entry.docNumber || '';
            record.receivedTxId = entry.transactionId;
            record.receivedTranType = entry.tranTypeCode;
            record.locationName = entry.locationName || '';
            record.status = 'In Stock';
          }
        } else if (entry.direction === 'out') {
          record.issuedVia = getTranTypeName(entry.tranTypeCode);
          record.issuedDate = entry.docDate || '';
          record.issuedDocNumber = entry.docNumber || '';
          record.issuedTxId = entry.transactionId;
          record.issuedTranType = entry.tranTypeCode;
          record.status = 'Issued';
        }
      });
    });

    const serialList = Object.values(serialMap);

    // Update summary for serial tab
    if (activeTab === 'serial') {
      const inStock = serialList.filter(s => s.status === 'In Stock').length;
      const issued = serialList.filter(s => s.status === 'Issued').length;
      const returned = serialList.filter(s => s.status === 'Returned').length;
      setSummary({
        totalTracked: serialList.length,
        inStock,
        issued,
        returned
      });
    }

    setSerialData(serialList);
  };

  const processBatchData = (entries, itemMasterMap) => {
    // Build a map of batch -> summary
    const batchMap = {};

    const sorted = [...entries].sort((a, b) => (a.docDate || '').localeCompare(b.docDate || ''));

    sorted.forEach(entry => {
      const batch = entry.batchNumber;
      if (!batch) return;

      const key = `${entry.itemId}__${batch}`;

      if (!batchMap[key]) {
        batchMap[key] = {
          batchNumber: batch,
          itemId: entry.itemId,
          itemCode: entry.itemCode || '',
          itemName: entry.itemName || '',
          expiryDate: entry.expiryDate || '',
          qtyIn: 0,
          qtyOut: 0,
          remaining: 0,
          status: 'In Stock',
          receivedVia: '',
          receivedDate: '',
          receivedDocNumber: '',
          lastIssuedVia: '',
          lastIssuedDate: '',
          lastIssuedDocNumber: '',
          locationName: entry.locationName || ''
        };
      }

      const record = batchMap[key];

      if (entry.direction === 'in') {
        record.qtyIn += entry.quantity || 0;
        if (!record.receivedDate) {
          record.receivedVia = getTranTypeName(entry.tranTypeCode);
          record.receivedDate = entry.docDate || '';
          record.receivedDocNumber = entry.docNumber || '';
          record.receivedTxId = entry.transactionId;
          record.receivedTranType = entry.tranTypeCode;
          record.locationName = entry.locationName || '';
        }
        if (entry.expiryDate) {
          record.expiryDate = entry.expiryDate;
        }
      } else if (entry.direction === 'out') {
        record.qtyOut += entry.quantity || 0;
        record.lastIssuedVia = getTranTypeName(entry.tranTypeCode);
        record.lastIssuedDate = entry.docDate || '';
        record.lastIssuedDocNumber = entry.docNumber || '';
        record.lastIssuedTxId = entry.transactionId;
        record.lastIssuedTranType = entry.tranTypeCode;
      }

      record.remaining = record.qtyIn - record.qtyOut;
      record.status = record.remaining > 0 ? 'In Stock' : 'Issued';
    });

    const batchList = Object.values(batchMap);

    // Update summary for batch tab
    if (activeTab === 'batch') {
      const inStock = batchList.filter(b => b.status === 'In Stock').length;
      const issued = batchList.filter(b => b.status === 'Issued').length;
      setSummary({
        totalTracked: batchList.length,
        inStock,
        issued,
        returned: 0
      });
    }

    setBatchData(batchList);
  };

  useEffect(() => {
    loadMasterData();
  }, [loadMasterData]);

  // Heavy stockFlow fetch is user-triggered only (Generate button) — no auto-load on
  // mount. Note: item/location/date filters are applied at fetch time, so changing
  // them after generating requires clicking Generate again. Status/search filters
  // stay client-side and apply instantly.
  const handleGenerate = () => {
    setHasGenerated(true);
    loadTrackingData();
  };

  // Recalculate summary when tab changes
  useEffect(() => {
    if (activeTab === 'serial') {
      const inStock = serialData.filter(s => s.status === 'In Stock').length;
      const issued = serialData.filter(s => s.status === 'Issued').length;
      const returned = serialData.filter(s => s.status === 'Returned').length;
      setSummary({ totalTracked: serialData.length, inStock, issued, returned });
    } else {
      const inStock = batchData.filter(b => b.status === 'In Stock').length;
      const issued = batchData.filter(b => b.status === 'Issued').length;
      setSummary({ totalTracked: batchData.length, inStock, issued, returned: 0 });
    }
  }, [activeTab, serialData, batchData]);

  // Backward memory: keep the generated view (filters + rows) in sync so Back
  // restores it with 0 re-reads. Tab/status/search are display-only over the
  // already-loaded data; item/location/date reflect the last generated fetch.
  useEffect(() => {
    if (!hasGenerated) return;
    saveMemory({
      activeTab, selectedItem, selectedLocation, selectedStatus,
      dateFrom, dateTo, searchQuery,
      serialData, batchData, summary, hasGenerated: true
    });
  }, [hasGenerated, activeTab, selectedItem, selectedLocation, selectedStatus,
      dateFrom, dateTo, searchQuery, serialData, batchData, summary, saveMemory]);

  // Filter items for dropdown — only show items with matching trackingType
  const filteredItems = items.filter(item => {
    if (activeTab === 'serial') return item.trackingType === 'Serial';
    if (activeTab === 'batch') return item.trackingType === 'Batch';
    return item.trackingType !== 'None';
  });

  // Apply search and status filters
  const getFilteredSerialData = () => {
    let data = serialData;

    if (selectedStatus !== 'all') {
      data = data.filter(s => s.status === selectedStatus);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      data = data.filter(s =>
        s.serialNumber.toLowerCase().includes(q) ||
        (s.itemCode || '').toLowerCase().includes(q) ||
        (s.itemName || '').toLowerCase().includes(q) ||
        (s.receivedDocNumber || '').toLowerCase().includes(q) ||
        (s.issuedDocNumber || '').toLowerCase().includes(q)
      );
    }

    return data;
  };

  const getFilteredBatchData = () => {
    let data = batchData;

    if (selectedStatus !== 'all') {
      data = data.filter(b => b.status === selectedStatus);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      data = data.filter(b =>
        b.batchNumber.toLowerCase().includes(q) ||
        (b.itemCode || '').toLowerCase().includes(q) ||
        (b.itemName || '').toLowerCase().includes(q) ||
        (b.receivedDocNumber || '').toLowerCase().includes(q) ||
        (b.lastIssuedDocNumber || '').toLowerCase().includes(q)
      );
    }

    return data;
  };

  const handleExport = () => {
    let csv = `${activeTab === 'serial' ? 'Serial Number' : 'Batch'} Tracking Report\n\n`;

    if (activeTab === 'serial') {
      csv += 'Serial #,Item Code,Item Name,Status,Received Via,Received Date,Received Doc #,Issued Via,Issued Date,Issued Doc #,Location\n';
      getFilteredSerialData().forEach(row => {
        csv += `"${row.serialNumber}",`;
        csv += `${row.itemCode || ''},`;
        csv += `"${(row.itemName || '').replace(/"/g, '""')}",`;
        csv += `${row.status},`;
        csv += `"${row.receivedVia}",`;
        csv += `${row.receivedDate || ''},`;
        csv += `${row.receivedDocNumber || ''},`;
        csv += `"${row.issuedVia}",`;
        csv += `${row.issuedDate || ''},`;
        csv += `${row.issuedDocNumber || ''},`;
        csv += `"${(row.locationName || '').replace(/"/g, '""')}"\n`;
      });
    } else {
      csv += 'Batch #,Item Code,Item Name,Expiry Date,Status,Qty In,Qty Out,Remaining,Received Via,Received Date,Received Doc #,Last Issued Via,Last Issued Date,Last Issued Doc #,Location\n';
      getFilteredBatchData().forEach(row => {
        csv += `"${row.batchNumber}",`;
        csv += `${row.itemCode || ''},`;
        csv += `"${(row.itemName || '').replace(/"/g, '""')}",`;
        csv += `${row.expiryDate || ''},`;
        csv += `${row.status},`;
        csv += `${row.qtyIn},`;
        csv += `${row.qtyOut},`;
        csv += `${row.remaining},`;
        csv += `"${row.receivedVia}",`;
        csv += `${row.receivedDate || ''},`;
        csv += `${row.receivedDocNumber || ''},`;
        csv += `"${row.lastIssuedVia}",`;
        csv += `${row.lastIssuedDate || ''},`;
        csv += `${row.lastIssuedDocNumber || ''},`;
        csv += `"${(row.locationName || '').replace(/"/g, '""')}"\n`;
      });
    }

    csv += `\nTotal Tracked:,${summary.totalTracked}\n`;
    csv += `In Stock:,${summary.inStock}\n`;
    csv += `Issued:,${summary.issued}\n`;
    if (activeTab === 'serial') csv += `Returned:,${summary.returned}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab === 'serial' ? 'Serial' : 'Batch'}_Tracking_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    window.print();
  };

  const renderSerialTable = () => {
    const data = getFilteredSerialData();

    if (data.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon><FiHash /></EmptyIcon>
          <EmptyText>
            {searchQuery || selectedItem !== 'all' || selectedStatus !== 'all'
              ? 'No serial numbers found matching your criteria'
              : 'No serial-tracked items found'}
          </EmptyText>
        </EmptyState>
      );
    }

    return (
      <Table>
        <Thead>
          <Tr>
            <Th>Serial #</Th>
            <Th>Item Code</Th>
            <Th>Item Name</Th>
            <Th align="center">Status</Th>
            <Th>Received Via</Th>
            <Th>Received Date</Th>
            <Th>Received Doc #</Th>
            <Th>Issued Via</Th>
            <Th>Issued Date</Th>
            <Th>Issued Doc #</Th>
            <Th>Location</Th>
          </Tr>
        </Thead>
        <Tbody>
          {data.map((row, idx) => (
            <Tr key={`${row.itemId}-${row.serialNumber}-${idx}`}>
              <Td><SerialBadge>{row.serialNumber}</SerialBadge></Td>
              <Td><ItemCode>{row.itemCode || '-'}</ItemCode></Td>
              <Td>{row.itemName || '-'}</Td>
              <Td align="center"><StatusBadge status={row.status}>{row.status}</StatusBadge></Td>
              <Td>{row.receivedVia ? <TypeBadge>{row.receivedVia}</TypeBadge> : '-'}</Td>
              <Td>{row.receivedDate ? formatDate(row.receivedDate) : '-'}</Td>
              <Td>{docCell(row.receivedDocNumber, row.receivedTranType, row.receivedTxId)}</Td>
              <Td>{row.issuedVia ? <TypeBadge>{row.issuedVia}</TypeBadge> : '-'}</Td>
              <Td>{row.issuedDate ? formatDate(row.issuedDate) : '-'}</Td>
              <Td>{docCell(row.issuedDocNumber, row.issuedTranType, row.issuedTxId)}</Td>
              <Td>{row.locationName || '-'}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  };

  const renderBatchTable = () => {
    const data = getFilteredBatchData();

    if (data.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon><FiBox /></EmptyIcon>
          <EmptyText>
            {searchQuery || selectedItem !== 'all' || selectedStatus !== 'all'
              ? 'No batches found matching your criteria'
              : 'No batch-tracked items found'}
          </EmptyText>
        </EmptyState>
      );
    }

    return (
      <Table>
        <Thead>
          <Tr>
            <Th>Batch #</Th>
            <Th>Item Code</Th>
            <Th>Item Name</Th>
            <Th>Expiry Date</Th>
            <Th align="center">Status</Th>
            <Th align="right">Qty In</Th>
            <Th align="right">Qty Out</Th>
            <Th align="right">Remaining</Th>
            <Th>Received Via</Th>
            <Th>Received Date</Th>
            <Th>Received Doc #</Th>
            <Th>Last Issued Via</Th>
            <Th>Last Issued Date</Th>
            <Th>Last Issued Doc #</Th>
            <Th>Location</Th>
          </Tr>
        </Thead>
        <Tbody>
          {data.map((row, idx) => (
            <Tr key={`${row.itemId}-${row.batchNumber}-${idx}`}>
              <Td><SerialBadge>{row.batchNumber}</SerialBadge></Td>
              <Td><ItemCode>{row.itemCode || '-'}</ItemCode></Td>
              <Td>{row.itemName || '-'}</Td>
              <Td>{row.expiryDate ? formatDate(row.expiryDate) : '-'}</Td>
              <Td align="center"><StatusBadge status={row.status}>{row.status}</StatusBadge></Td>
              <AmountCell align="right">{row.qtyIn}</AmountCell>
              <AmountCell align="right">{row.qtyOut}</AmountCell>
              <AmountCell align="right">{row.remaining}</AmountCell>
              <Td>{row.receivedVia ? <TypeBadge>{row.receivedVia}</TypeBadge> : '-'}</Td>
              <Td>{row.receivedDate ? formatDate(row.receivedDate) : '-'}</Td>
              <Td>{docCell(row.receivedDocNumber, row.receivedTranType, row.receivedTxId)}</Td>
              <Td>{row.lastIssuedVia ? <TypeBadge>{row.lastIssuedVia}</TypeBadge> : '-'}</Td>
              <Td>{row.lastIssuedDate ? formatDate(row.lastIssuedDate) : '-'}</Td>
              <Td>{docCell(row.lastIssuedDocNumber, row.lastIssuedTranType, row.lastIssuedTxId)}</Td>
              <Td>{row.locationName || '-'}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  };

  return (
    <PageLayout pageTitle="Batch & Serial Tracking" breadcrumb="Reports / Inventory Reports / Serial Tracking">
      <TabContainer>
        <Tab active={activeTab === 'serial'} onClick={() => { setActiveTab('serial'); setSelectedItem('all'); setSearchQuery(''); setSelectedStatus('all'); }}>
          <FiHash size={16} /> Serial Number Tracking
        </Tab>
        <Tab active={activeTab === 'batch'} onClick={() => { setActiveTab('batch'); setSelectedItem('all'); setSearchQuery(''); setSelectedStatus('all'); }}>
          <FiBox size={16} /> Batch Tracking
        </Tab>
      </TabContainer>

      {hasGenerated && !loading && (
      <SummaryCards>
        <SummaryCard>
          <SummaryIcon color={colors.primary.main}>
            <FiPackage />
          </SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Total {activeTab === 'serial' ? 'Serials' : 'Batches'}</SummaryLabel>
            <SummaryValue>{summary.totalTracked}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color={colors.success.main}>
            <FiCheckCircle />
          </SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>In Stock</SummaryLabel>
            <SummaryValue>{summary.inStock}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
        <SummaryCard>
          <SummaryIcon color={colors.error.main}>
            <FiXCircle />
          </SummaryIcon>
          <SummaryInfo>
            <SummaryLabel>Issued / Sold</SummaryLabel>
            <SummaryValue>{summary.issued}</SummaryValue>
          </SummaryInfo>
        </SummaryCard>
        {activeTab === 'serial' && (
          <SummaryCard>
            <SummaryIcon color={colors.warning.main}>
              <FiRotateCcw />
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Returned</SummaryLabel>
              <SummaryValue>{summary.returned}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        )}
      </SummaryCards>
      )}

      <FiltersCard>
        <FiltersRow>
          <FilterGroup>
            <Label>Item</Label>
            <ItemSelect
              items={filteredItems}
              selectedItemId={selectedItem}
              onChange={setSelectedItem}
              allOption={{ value: 'all', label: `All ${activeTab === 'serial' ? 'Serial' : 'Batch'} Items` }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Location</Label>
            <Select value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)}>
              <option value="all">All Locations</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.locationName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Status</Label>
            <Select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
              <option value="all">All</option>
              <option value="In Stock">In Stock</option>
              <option value="Issued">Issued / Sold</option>
              {activeTab === 'serial' && <option value="Returned">Returned</option>}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>From Date</Label>
            <DateInput
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>To Date</Label>
            <DateInput
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </FilterGroup>

          <SearchInput>
            <SearchIcon><FiSearch /></SearchIcon>
            <Input
              type="text"
              placeholder={`Search by ${activeTab === 'serial' ? 'serial' : 'batch'} #, item, or doc #...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </SearchInput>

          <ButtonGroup>
            <Button onClick={handleExport} disabled={(activeTab === 'serial' ? serialData : batchData).length === 0}>
              <FiDownload /> Export
            </Button>
            <Button onClick={handlePrint} disabled={(activeTab === 'serial' ? serialData : batchData).length === 0}>
              <FiPrinter /> Print
            </Button>
            <Button primary onClick={handleGenerate} disabled={loading}>
              <FiHash /> {loading ? 'Loading...' : 'Generate'}
            </Button>
          </ButtonGroup>
        </FiltersRow>
      </FiltersCard>

      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyText>Loading tracking data...</EmptyText>
            </EmptyState>
          ) : !hasGenerated ? (
            <EmptyState>
              <EmptyIcon>{activeTab === 'serial' ? <FiHash /> : <FiBox />}</EmptyIcon>
              <EmptyText>Select your filters and click Generate to view tracking data</EmptyText>
            </EmptyState>
          ) : activeTab === 'serial' ? renderSerialTable() : renderBatchTable()}
        </TableContainer>
      </ContentCard>
    </PageLayout>
  );
};

export default SerialTracking;
